/**
 * MockDataProvider — DEMO ONLY. Keeps fictional data in memory and, for convenience, in this
 * browser's localStorage under `easysaas.demo.v1`, so edits survive a reload. This is not a
 * database and must never be used for real data; production uses ApiDataProvider.
 *
 * It applies the same write rules as the original app (see "Server rule" in DataProvider.ts),
 * so the demo behaves like the product.
 *
 * Demo switches (URL query, read once at start-up — useful to show loading / empty / error states):
 *   ?mockDelay=1500        every call waits 1.5 s (default 120 ms)
 *   ?mockEmpty=1           start with no data (not saved)
 *   ?mockFail=projects,logs  those resources fail (projects, members, logs, updates, settings)
 */
import type {
  Backup,
  DeadlineChangeInput,
  ImportSummary,
  Member,
  MemberInput,
  ParsedDay,
  Project,
  ProjectInput,
  ProjectPatch,
  ProjectUpdate,
  ProjectUpdateFilter,
  ProjectUpdateInput,
  Settings,
  WorkLog,
  WorkLogFilter,
  WorkLogInput,
} from '@/types/models';
import { DataError, type DataProvider, type ProviderCapabilities } from '@/services/DataProvider';
import { deadline } from '@/domain/projects';
import { today } from '@/domain/format';
import { matchMember, matchProject } from '@/domain/docxImport';
import { normalizeBackup } from '@/domain/backup';
import { buildDemoData, emptyData } from './seed';

export const DEMO_STORAGE_KEY = 'easysaas.demo.v1';

export interface MockOptions {
  delayMs?: number;
  empty?: boolean;
  fail?: string[];
  /** Persist to localStorage (default true in the browser, false in tests) */
  persist?: boolean;
  /** Use this data instead of the demo seed */
  initial?: Backup;
}

export function mockOptionsFromUrl(search = typeof window !== 'undefined' ? window.location.search : ''): MockOptions {
  const q = new URLSearchParams(search);
  const d = Number(q.get('mockDelay'));
  return {
    delayMs: Number.isFinite(d) && q.has('mockDelay') ? d : undefined,
    empty: q.get('mockEmpty') === '1',
    fail: (q.get('mockFail') || '').split(',').map((s) => s.trim()).filter(Boolean),
  };
}

const clone = <T,>(v: T): T => (v === undefined ? v : JSON.parse(JSON.stringify(v)));
let seq = 0;
const newId = (prefix: string) => `${prefix}-${Date.now().toString(36)}${(++seq).toString(36)}${Math.random().toString(36).slice(2, 6)}`;

export class MockDataProvider implements DataProvider {
  readonly mode = 'mock' as const;
  readonly capabilities: ProviderCapabilities = { backup: true, resetDemo: true, deleteAll: true, serverAnalytics: false };
  private db: Backup;
  private readonly delayMs: number;
  private readonly fail: Set<string>;
  private readonly persist: boolean;

  constructor(opts: MockOptions = {}) {
    this.delayMs = opts.delayMs ?? 120;
    this.fail = new Set(opts.fail || []);
    this.persist = (opts.persist ?? typeof localStorage !== 'undefined') && !opts.empty && !opts.initial;
    this.db = opts.initial ? normalizeBackup(clone(opts.initial)) : opts.empty ? emptyData() : this.load();
  }

  /* ---------- plumbing ---------- */
  private load(): Backup {
    if (this.persist) {
      try {
        const s = localStorage.getItem(DEMO_STORAGE_KEY);
        if (s) return normalizeBackup(JSON.parse(s));
      } catch {
        /* fall through to fresh demo data */
      }
    }
    const d = buildDemoData();
    this.db = d;
    this.save();
    return d;
  }
  private save() {
    if (!this.persist) return;
    try {
      localStorage.setItem(DEMO_STORAGE_KEY, JSON.stringify(this.db));
    } catch {
      /* storage full or blocked: keep working in memory */
    }
  }
  private async run<T>(resource: string, fn: () => T): Promise<T> {
    if (this.delayMs > 0) await new Promise((r) => setTimeout(r, this.delayMs));
    if (this.fail.has(resource)) throw new DataError(`Demo error: ${resource} could not be loaded.`, 503);
    return clone(fn());
  }
  private project(id: string): Project {
    const p = this.db.projects.find((x) => x.id === id);
    if (!p) throw new DataError('Project not found.', 404);
    return p;
  }
  private member(id: string): Member {
    const m = this.db.members.find((x) => x.id === id);
    if (!m) throw new DataError('Member not found.', 404);
    return m;
  }

  /* ---------- projects ---------- */
  listProjects() {
    return this.run('projects', () => this.db.projects);
  }
  getProject(id: string) {
    return this.run('projects', () => this.db.projects.find((p) => p.id === id) || null);
  }
  createProject(input: ProjectInput) {
    return this.run('projects', () => {
      if (!input.name?.trim()) throw new DataError('Project Name is required.', 422);
      const p: Project = {
        id: newId('p'),
        name: input.name.trim(),
        service: input.service || '',
        lead: input.lead || '',
        status: input.status || 'In Progress',
        planned: input.planned || '',
        revised: '',
        delayReason: '',
        completed: input.completed || '',
        pending: input.pending || '',
        extensions: [],
        links: [],
        members: [],
        metricKeys: [],
        metricKinds: {},
      };
      this.db.projects.push(p);
      this.save();
      return p;
    });
  }
  updateProject(id: string, patch: ProjectPatch) {
    return this.run('projects', () => {
      const p = this.project(id);
      Object.assign(p, patch, { id });
      this.save();
      return p;
    });
  }
  deleteProject(id: string) {
    return this.run('projects', () => {
      this.project(id);
      this.db.projects = this.db.projects.filter((p) => p.id !== id);
      this.db.logs = this.db.logs.filter((l) => l.projectId !== id);
      this.db.updates = this.db.updates.filter((u) => u.projectId !== id);
      this.save();
    });
  }
  changeDeadline(id: string, input: DeadlineChangeInput) {
    return this.run('projects', () => {
      const p = this.project(id);
      const cur = deadline(p);
      const reason = (input.reason || '').trim();
      if (!input.to || (cur && input.to <= cur)) throw new DataError(cur ? 'New deadline must be a date after the current deadline.' : 'Deadline is required.', 422);
      if (reason.replace(/\s+/g, ' ').length < 10) throw new DataError('A proper reason is required (at least 10 characters).', 422);
      p.extensions = p.extensions || [];
      if (cur) {
        p.extensions.push({ on: today(), from: cur, to: input.to, reason });
        p.revised = input.to;
        p.delayReason = reason;
      } else {
        p.planned = input.to;
        p.deadlineBasis = reason;
      }
      this.save();
      return p;
    });
  }

  /* ---------- members ---------- */
  listMembers() {
    return this.run('members', () => this.db.members);
  }
  getMember(id: string) {
    return this.run('members', () => this.db.members.find((m) => m.id === id) || null);
  }
  createMember(input: MemberInput) {
    return this.run('members', () => {
      const name = input.name.trim();
      if (!name) throw new DataError('Full Name is required.', 422);
      if (this.db.members.some((x) => x.name.toLowerCase() === name.toLowerCase())) throw new DataError('A member with this name already exists.', 409);
      const m: Member = { id: newId('m'), name, role: (input.role || '').trim() };
      this.db.members.push(m);
      this.save();
      return m;
    });
  }
  updateMember(id: string, patch: Partial<MemberInput>) {
    return this.run('members', () => {
      const m = this.member(id);
      Object.assign(m, patch, { id });
      this.save();
      return m;
    });
  }
  deleteMember(id: string) {
    return this.run('members', () => {
      this.member(id);
      this.db.members = this.db.members.filter((x) => x.id !== id);
      this.db.logs = this.db.logs.filter((l) => l.memberId !== id);
      this.db.projects.forEach((p) => (p.members = p.members.filter((x) => x !== id)));
      this.db.settings.generalMembers = (this.db.settings.generalMembers || []).filter((x) => x !== id);
      this.save();
    });
  }

  /* ---------- work logs ---------- */
  listWorkLogs(f: WorkLogFilter = {}) {
    return this.run('logs', () =>
      this.db.logs.filter((l) => (!f.from || l.date >= f.from) && (!f.to || l.date <= f.to) && (f.projectId === undefined || l.projectId === f.projectId) && (!f.memberId || l.memberId === f.memberId)),
    );
  }
  private addToProject(projectId: string, memberId: string) {
    if (!projectId) return;
    const p = this.db.projects.find((x) => x.id === projectId);
    if (p && !p.members.includes(memberId)) p.members.push(memberId);
  }
  createWorkLog(input: WorkLogInput) {
    return this.run('logs', () => {
      const h = Number(input.hours);
      if (!input.date || !input.memberId || !Number.isFinite(h) || h < 0 || h > 24) throw new DataError('Date, Member and Hours (0–24) are required.', 422);
      const l: WorkLog = { id: newId('wl'), date: input.date, projectId: input.projectId || '', memberId: input.memberId, hours: h, work: (input.work || '').trim() };
      this.addToProject(l.projectId, l.memberId);
      this.db.logs.push(l);
      this.save();
      return l;
    });
  }
  updateWorkLog(id: string, patch: Partial<WorkLogInput>) {
    return this.run('logs', () => {
      const l = this.db.logs.find((x) => x.id === id);
      if (!l) throw new DataError('Work log not found.', 404);
      Object.assign(l, patch, { id });
      this.addToProject(l.projectId, l.memberId);
      this.save();
      return l;
    });
  }
  deleteWorkLog(id: string) {
    return this.run('logs', () => {
      this.db.logs = this.db.logs.filter((x) => x.id !== id);
      this.save();
    });
  }

  /* ---------- project updates ---------- */
  listProjectUpdates(f: ProjectUpdateFilter = {}) {
    return this.run('updates', () => this.db.updates.filter((u) => (!f.from || u.date >= f.from) && (!f.to || u.date <= f.to) && (!f.projectId || u.projectId === f.projectId)));
  }
  private assertNoDuplicate(projectId: string, date: string, exceptId?: string) {
    if (this.db.updates.some((x) => x.projectId === projectId && x.date === date && x.id !== exceptId))
      throw new DataError('A project log for this date already exists. Edit it instead.', 409);
  }
  createProjectUpdate(input: ProjectUpdateInput) {
    return this.run('updates', () => {
      this.assertNoDuplicate(input.projectId, input.date);
      const u: ProjectUpdate = { id: newId('pu'), date: input.date, projectId: input.projectId, note: (input.note || '').trim(), metrics: input.metrics || [] };
      this.db.updates.push(u);
      this.save();
      return u;
    });
  }
  updateProjectUpdate(id: string, patch: Partial<ProjectUpdateInput>) {
    return this.run('updates', () => {
      const u = this.db.updates.find((x) => x.id === id);
      if (!u) throw new DataError('Project log not found.', 404);
      this.assertNoDuplicate(patch.projectId ?? u.projectId, patch.date ?? u.date, id);
      Object.assign(u, patch, { id });
      this.save();
      return u;
    });
  }
  deleteProjectUpdate(id: string) {
    return this.run('updates', () => {
      this.db.updates = this.db.updates.filter((x) => x.id !== id);
      this.save();
    });
  }

  /* ---------- settings ---------- */
  getSettings() {
    return this.run('settings', () => this.db.settings);
  }
  updateSettings(patch: Partial<Settings>) {
    return this.run('settings', () => {
      Object.assign(this.db.settings, patch);
      this.save();
      return this.db.settings;
    });
  }

  /* ---------- bulk ---------- */
  importDailyUpdate(days: ParsedDay[]) {
    return this.run('logs', (): ImportSummary => {
      const newMembers = new Set<string>();
      const newProjects = new Set<string>();
      let workLogs = 0;
      let projectLogs = 0;
      days.forEach((d) => {
        const apply = (b: ParsedDay['general'], pid: string) => {
          b.logs.forEach((l) => {
            let m = matchMember(this.db.members, l.name);
            if (!m) {
              m = { id: newId('m'), name: l.name, role: '' };
              this.db.members.push(m);
              newMembers.add(l.name);
            }
            const mid = m.id;
            this.db.logs = this.db.logs.filter((x) => !(x.date === d.date && x.memberId === mid && (x.projectId || '') === (pid || '')));
            this.db.logs.push({ id: newId('wl'), date: d.date, projectId: pid || '', memberId: mid, hours: l.hours, work: l.work });
            this.addToProject(pid, mid);
            workLogs++;
          });
          if (pid && (b.notes.length || b.metrics.length)) {
            this.db.updates = this.db.updates.filter((u) => !(u.date === d.date && u.projectId === pid));
            this.db.updates.push({ id: newId('pu'), date: d.date, projectId: pid, note: b.notes.join(' '), metrics: b.metrics });
            projectLogs++;
          }
        };
        apply(d.general, '');
        Object.entries(d.projects).forEach(([n, b]) => {
          let p = matchProject(this.db.projects, n);
          if (!p) {
            p = { id: newId('p'), name: n, service: '', lead: '', status: 'In Progress', planned: '', revised: '', completed: '', pending: '', delayReason: '', extensions: [], links: [], members: [], metricKeys: [], metricKinds: {} };
            this.db.projects.push(p);
            newProjects.add(n);
          }
          apply(b, p.id);
        });
      });
      this.save();
      return { days: days.length, workLogs, projectLogs, newMembers: [...newMembers], newProjects: [...newProjects] };
    });
  }
  exportBackup() {
    return this.run('backup', () => this.db);
  }
  importBackup(backup: Backup) {
    return this.run('backup', () => {
      this.db = normalizeBackup(clone(backup));
      this.save();
    });
  }
  resetDemoData() {
    return this.run('backup', () => {
      this.db = buildDemoData();
      this.save();
    });
  }
  deleteAllData() {
    return this.run('backup', () => {
      this.db = emptyData();
      this.save();
    });
  }
}
